"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Icon } from "@/components/ui/Icon";

type Step = "email" | "code";

export function LoginForm() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // Honeypot
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  /*
   * Verbleibende Sperrfrist bis ein neuer Code angefordert werden darf.
   * Der Server erlaubt höchstens einen Code alle 30 Sekunden je Adresse;
   * hier läuft nur die Anzeige mit, damit niemand vergeblich klickt.
   */
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const codeRef = useRef<HTMLInputElement>(null);

  async function requestCode(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch("/api/auth/request-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error ?? "Etwas ist schiefgelaufen. Bitte erneut versuchen.");
        return;
      }
      setStep("code");
      setInfo(`Wir haben einen 6-stelligen Code an ${email} geschickt (falls ein Konto existiert).`);
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch {
      setError("Netzwerkfehler. Bitte Verbindung prüfen.");
    } finally {
      setBusy(false);
    }
  }

  async function verify(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error ?? "Der Code ist ungültig.");
        return;
      }
      router.push(data.redirect ?? "/portal");
      router.refresh();
    } catch {
      setError("Netzwerkfehler. Bitte Verbindung prüfen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-6 md:p-8">
      {step === "email" && (
        <form onSubmit={requestCode} className="flex flex-col gap-4">
          <div>
            <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-fg-muted">
              E-Mail-Adresse
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre@firma.at"
              className="w-full rounded-xl border border-line bg-surface-2 px-4 py-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          {/* Honeypot – für Menschen unsichtbar, Bots füllen ihn aus. */}
          <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
            <label>
              Website (bitte leer lassen)
              <input name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" />
            </label>
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <SubmitButton busy={busy} label="Code anfordern" busyLabel="Wird gesendet …" />
          <p className="text-center text-xs text-fg-subtle">
            Sie erhalten einen einmaligen Sicherheitscode per E-Mail. Kein Passwort nötig.
          </p>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={verify} className="flex flex-col gap-4">
          {info && (
            <p className="rounded-xl border border-brand-400/20 bg-brand-500/10 px-4 py-3 text-sm text-brand-ink">
              {info}
            </p>
          )}
          <div>
            <label htmlFor="login-code" className="mb-1.5 block text-sm font-medium text-fg-muted">
              6-stelliger Code
            </label>
            <input
              id="login-code"
              ref={codeRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              className="w-full rounded-xl border border-line bg-surface-2 px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-fg placeholder:text-fg-subtle focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <SubmitButton busy={busy} label="Anmelden" busyLabel="Prüfe …" disabled={code.length !== 6} />
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={() => { setStep("email"); setCode(""); setError(""); }} className="text-fg-subtle hover:text-fg">
              ← andere E-Mail
            </button>
            <button type="button" onClick={() => requestCode()} disabled={busy} className="text-brand-ink hover:text-fg disabled:opacity-50">
              Code erneut senden
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-danger">
      {children}
    </p>
  );
}

function SubmitButton({ busy, label, busyLabel, disabled }: { busy: boolean; label: string; busyLabel: string; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-500 px-7 py-3.5 font-semibold text-white transition-all hover:bg-brand-400 disabled:pointer-events-none disabled:opacity-50"
    >
      {busy ? (
        <>
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-white" />
          {busyLabel}
        </>
      ) : (
        <>
          {label} <Icon name="arrow-right" size={18} />
        </>
      )}
    </button>
  );
}
